import React, { useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingBag, Trash2, Plus, Minus, ArrowRight } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { shopData } from './pages/site/shopData';
import { bookData } from './pages/site/bookData';
function formatPrice(cents: number): string {
  return `${(cents / 100).toFixed(2)}`;
}
function getCartItemImage(item: {
  productId: string;
  name: string;
  type: string;
}): string | undefined {
  const name = item.name?.trim().toLowerCase();
  if (item.type === 'book') {
    const b = bookData.find(x => x.id === item.productId) ?? bookData.find(x => x.title?.trim().toLowerCase() === name);
    return b?.coverImage;
  }
  const p = shopData.find(s => s.id === item.productId) ?? shopData.find(s => s.name?.trim().toLowerCase() === name);
  return p?.image;
}
export function CartDrawer() {
  const {
    items,
    subtotal,
    itemCount,
    isOpen,
    closeCart,
    removeItem,
    updateQuantity
  } = useCart();
  const {
    user
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isShopOrBooks = location.pathname === '/shop' || location.pathname === '/books';
  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  // Close on escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeCart();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, closeCart]);
  const handleCheckout = () => {
    closeCart();
    if (!user) {
      navigate('/login', {
        state: {
          from: '/checkout'
        }
      });
      return;
    }
    navigate('/checkout');
  };
  return <AnimatePresence data-id="element-42">
      {isOpen && isShopOrBooks && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-charcoal/70 backdrop-blur-sm z-[60]" onClick={closeCart} aria-hidden="true" data-id="element-43" />

          {/* Panel */}
          <motion.aside initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        duration: 0.3,
        ease: [0.2, 0.65, 0.3, 0.9]
      }} className="fixed top-0 right-0 h-full w-full sm:w-[440px] bg-[#F9F9F9] z-[70] shadow-2xl flex flex-col" role="dialog" aria-label="Shopping cart" data-id="element-44">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E5E5] bg-white" data-id="element-45">
              <div className="flex items-center gap-3" data-id="element-46">
                <ShoppingBag className="w-5 h-5 text-tealslate" data-id="element-47" />
                <h2 className="text-lg font-bold text-[#111111] tracking-tight" data-id="element-48">
                  Your Cart
                </h2>
              </div>
              <button onClick={closeCart} className="p-2 text-[#9A9A90] hover:text-tealslate transition-colors" aria-label="Close cart" data-id="element-49">
                <X className="w-5 h-5" data-id="element-50" />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-51">
              {items.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-center" data-id="element-52">
                  <div className="w-16 h-16 bg-white border border-[#E5E5E5] shadow-sm rounded-full flex items-center justify-center mb-5" data-id="element-53">
                    <ShoppingBag className="w-7 h-7 text-[#9A9A90]" data-id="element-54" />
                  </div>
                  <h3 className="text-xl font-bold text-[#111111] tracking-tight mb-3" data-id="element-55">
                    Your cart is empty.
                  </h3>
                  <p className="text-sm font-medium text-[#5A5A5A] mb-8 max-w-xs leading-relaxed" data-id="element-56">
                    Browse the bookshop or shop to add items.
                  </p>
                  <div className="flex flex-col gap-3 w-full max-w-xs" data-id="element-57">
                    <Link to="/books" onClick={closeCart} className="px-6 py-3 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm inline-flex items-center justify-center gap-2" data-id="element-58">
                      Browse Books <ArrowRight className="w-4 h-4" data-id="element-59" />
                    </Link>
                    <Link to="/shop" onClick={closeCart} className="px-6 py-3 bg-white border border-[#E5E5E5] text-[#111111] text-sm font-bold hover:border-tealslate hover:text-tealslate transition-colors rounded-md shadow-sm inline-flex items-center justify-center gap-2" data-id="element-60">
                      Shop Supplements <ArrowRight className="w-4 h-4" data-id="element-61" />
                    </Link>
                  </div>
                </div> : <ul className="bg-white border border-[#E5E5E5]" data-id="element-62">
                  {items.map(item => <li key={item.productId} className="px-4 py-4 border-b border-[#E5E5E5] last:border-b-0 flex gap-3" data-id="element-63">
                      {/* Image */}
                      {getCartItemImage(item) && <div className="w-20 h-20 shrink-0 flex items-center justify-center" data-id="element-64">
                          <img src={getCartItemImage(item)} alt="" className="max-w-full max-h-full object-contain" data-id="element-65" />
                        </div>}

                      {/* Details */}
                      <div className="flex-1 min-w-0" data-id="element-66">
                        <p className="text-sm font-bold text-[#111111] leading-snug" data-id="element-67">
                          {item.name}
                        </p>
                        <p className="text-xs text-tealslate font-medium mt-0.5" data-id="element-68">
                          In Stock
                        </p>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#9A9A90] mt-0.5" data-id="element-69">
                          {item.type === 'book' ? 'Digital Book' : 'Supplement'}
                        </p>

                        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-3" data-id="element-70">
                          <div className="flex items-center border border-[#E5E5E5] rounded-md overflow-hidden" data-id="element-71">
                            <button onClick={() => updateQuantity(item.productId, item.quantity - 1)} className="px-2.5 py-1.5 text-[#5A5A5A] hover:bg-[#F9F9F9] transition-colors" aria-label="Decrease quantity" data-id="element-72">
                              <Minus className="w-3 h-3" data-id="element-73" />
                            </button>
                            <span className="px-3 text-sm font-bold text-[#111111] min-w-[1.75rem] text-center" data-id="element-74">
                              {item.quantity}
                            </span>
                            <button onClick={() => updateQuantity(item.productId, item.quantity + 1)} className="px-2.5 py-1.5 text-[#5A5A5A] hover:bg-[#F9F9F9] transition-colors" aria-label="Increase quantity" data-id="element-75">
                              <Plus className="w-3 h-3" data-id="element-76" />
                            </button>
                          </div>
                          <button onClick={() => removeItem(item.productId)} className="text-sm font-medium text-[#5A5A5A] hover:text-burgundy transition-colors inline-flex items-center gap-1" data-id="element-77">
                            <Trash2 className="w-3.5 h-3.5" data-id="element-78" />
                            Delete
                          </button>
                        </div>
                      </div>

                      {/* Price */}
                      <div className="shrink-0 text-right" data-id="element-79">
                        <p className="text-sm font-bold text-[#111111]" data-id="element-80">
                          {formatPrice(item.unitPrice * item.quantity)}
                        </p>
                      </div>
                    </li>)}
                </ul>}
            </div>

            {/* Footer */}
            {items.length > 0 && <div className="border-t border-[#E5E5E5] bg-white px-6 py-6 space-y-4" data-id="element-81">
                <div className="text-right" data-id="element-82">
                  <p className="text-base text-[#111111]" data-id="element-83">
                    Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'items'}):{' '}
                    <span className="font-bold" data-id="element-84">{formatPrice(subtotal)}</span>
                  </p>
                </div>
                <p className="text-sm text-[#5A5A5A]" data-id="element-85">
                  Shipping and taxes calculated at checkout.
                </p>
                {/* PRE-ORDER MODE — Real Checkout button hidden until launch.
                    To restore: delete the Pre-Order button below and uncomment this one.
                 <button
                  onClick={handleCheckout}
                  className="w-full px-8 py-4 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm inline-flex items-center justify-center gap-3"
                 >
                  Checkout <ArrowRight className="w-4 h-4" />
                 </button>
                 */}
                <button onClick={handleCheckout} className="w-full px-8 py-4 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm inline-flex items-center justify-center gap-3" data-id="element-86">
                  Pre-Order <ArrowRight className="w-4 h-4" data-id="element-87" />
                </button>
              </div>}
          </motion.aside>
        </>}
    </AnimatePresence>;
}